import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { loadOAuthConsent, clearOAuthConsent } from '@/utils/wizardStorage';
import { settingsApi } from '@/services/settings';
import Alert from '@/components/ui/Alert';
import Button from '@/components/ui/Button';

/**
 * OAuth Callback Page
 *
 * WP-AUTH-7: Handles OAuth provider callbacks (Microsoft, Apple).
 * WP-SEC-7: Tokens are now delivered via httpOnly cookies (not URL).
 *
 * Flow:
 * 1. Backend redirects here with success=true in query params
 * 2. Tokens are set as httpOnly cookies by the backend
 * 3. Frontend calls checkAuth() to verify authentication via cookie
 * 4. On success, redirect to dashboard
 *
 * Legacy support:
 * - Still handles URL fragment tokens for backward compatibility
 * - MFA flow still uses URL fragment (requires mfa_token for challenge)
 *
 * Note: Google OAuth uses a different flow (frontend handles token via SDK)
 */
export default function OAuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { setUser, checkAuth } = useAuthStore();

  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Check for error in query params (OAuth errors come as query params)
        const errorParam = searchParams.get('error');
        const errorMessage = searchParams.get('message');
        const provider = searchParams.get('provider') || 'OAuth';

        if (errorParam) {
          // WP-SEC-27: the provider identity is linked to an account whose
          // email is not verified, and this sign-in did not prove that address.
          if (errorParam === 'email_not_verified') {
            setError(
              'Check your email to verify your account, then sign in again.'
            );
            setProcessing(false);
            return;
          }
          setError(
            errorMessage ||
            `Authentication with ${provider} failed: ${errorParam}`
          );
          setProcessing(false);
          return;
        }

        // WP-SEC-7: Check for new cookie-based success flow
        // Backend now redirects with ?success=true and sets httpOnly cookies
        const success = searchParams.get('success') === 'true';
        const isNewUserQuery = searchParams.get('is_new_user') === 'true';

        // Parse hash fragment for legacy flow and MFA
        const hash = location.hash.substring(1); // Remove leading #
        const hashParams = hash ? new URLSearchParams(hash) : null;

        // WP-AUTH-7: Check if MFA is required (still uses URL fragment)
        const requiresMfa = hashParams?.get('requires_mfa') === 'true';
        const mfaToken = hashParams?.get('mfa_token');
        const isNewUserHash = hashParams?.get('is_new_user') === 'true';

        if (requiresMfa && mfaToken) {
          // Redirect to MFA challenge page
          const deviceId = hashParams?.get('device_id');

          navigate('/mfa', {
            replace: true,
            state: {
              mfaToken,
              isNewUser: isNewUserHash,
              deviceId,
              from: { pathname: '/dashboard' },
            },
          });
          return;
        }

        // WP-SEC-7: New secure cookie-based flow
        // Backend sets tokens as httpOnly cookies and redirects with success=true
        if (success) {
          // Tokens are in httpOnly cookies - verify auth via API
          // checkAuth() will read the access token from cookie and fetch user
          await checkAuth();

          // PII minimization: Default new OAuth users to "User 1" for privacy
          // Users can change this in settings - this prevents exposing their real name
          if (isNewUserQuery) {
            try {
              await settingsApi.updateProfile({ name: 'User 1' });
            } catch {
              // Non-critical - user can change in settings
            }

            // WP-AUTH-13 Phase 6: Check for orphaned consent data
            const orphanedConsent = loadOAuthConsent();
            if (orphanedConsent) {
              console.warn('Found orphaned OAuth consent data for new user - clearing');
            }
          }

          // WP-AUTH-13: Clear OAuth consent from sessionStorage
          clearOAuthConsent();

          // Navigate to dashboard
          navigate('/dashboard', { replace: true });
          return;
        }

        // Legacy flow: Extract access token from URL fragment
        // This supports older OAuth flows that may still use URL fragments
        const accessToken = hashParams?.get('access_token');

        if (!accessToken && !hash) {
          setError('No authentication data received');
          setProcessing(false);
          return;
        }

        if (accessToken) {
          // Legacy: Import setAccessToken only if needed for backward compatibility
          const { setAccessToken } = await import('@/services/api');
          setAccessToken(accessToken);
        }

        // Fetch current user to populate auth store
        await checkAuth();

        // WP-AUTH-13 Phase 6: Check for orphaned consent data
        const isNewUser = isNewUserQuery || isNewUserHash;
        if (isNewUser) {
          // PII minimization: Default new OAuth users to "User 1" for privacy
          // Users can change this in settings - this prevents exposing their real name
          try {
            await settingsApi.updateProfile({ name: 'User 1' });
          } catch {
            // Non-critical - user can change in settings
          }

          const orphanedConsent = loadOAuthConsent();
          if (orphanedConsent) {
            console.warn('Found orphaned OAuth consent data for new user - clearing');
          }
        }

        // WP-AUTH-13: Clear OAuth consent from sessionStorage
        clearOAuthConsent();

        // Navigate to dashboard
        navigate('/dashboard', { replace: true });
      } catch (err) {
        // Log error in development only (no PII)
        if (import.meta.env.DEV) {
          console.error('OAuth callback error:', err);
        }
        setError('Failed to complete authentication. Please try again.');
        setProcessing(false);
      }
    };

    handleCallback();
  }, [searchParams, location.hash, navigate, setUser, checkAuth]);

  // Processing state
  if (processing) {
    return (
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Completing sign in...
        </h2>
        <p className="text-gray-600">
          Please wait while we verify your authentication.
        </p>
      </div>
    );
  }

  // Error state
  return (
    <div className="text-center">
      <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
        <svg
          className="h-6 w-6 text-red-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">
        Authentication Failed
      </h2>

      {error && (
        <Alert variant="error" className="mb-6 text-left">
          {error}
        </Alert>
      )}

      <div className="space-y-4">
        <Button onClick={() => navigate('/login')} className="w-full">
          Back to sign in
        </Button>
        <p className="text-sm text-gray-500">
          If this problem persists, please contact support.
        </p>
      </div>
    </div>
  );
}
